import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, Heart, Navigation, GitCompare } from 'lucide-react';
import { useFavorites } from '../../hooks/use-favorites';
import { useComparison } from '../../hooks/use-comparison';

/**
 * ListingCard - Reusable card component for farm/market listings
 * Supports both grid (vertical) and list (horizontal) view layouts
 * Integrated with Phase 2 favorites and comparison features
 */
const ListingCard = ({ listing, variant = 'grid' }) => {
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const { isInComparison, toggleItem, canAdd } = useComparison();

  // Check if this listing is favorited or in comparison
  const isFavorited = isFavorite(listing.id);
  const isComparing = isInComparison(listing.id);

  const {
    id,
    name,
    slug,
    type,
    description,
    address,
    city,
    phone,
    images = [],
    rating = 0,
    reviewCount = 0,
    products = [],
    tags = [],
    featured = false,
    distance
  } = listing;

  // Get the main image or use placeholder
  const placeholderImage = '/images/placeholders/produce-fallback.svg';
  const mainImage = images[0] || listing.image || listing.logo || placeholderImage;

  // Truncate description for card view
  const truncateDescription = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Format rating display
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star
            key={i}
            size={14}
            className="listing-card__star listing-card__star--filled"
            fill="#f59e0b"
            stroke="#f59e0b"
          />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star
            key={i}
            size={14}
            className="listing-card__star listing-card__star--half"
            fill="#f59e0b"
            stroke="#f59e0b"
            style={{ clipPath: 'inset(0 50% 0 0)' }}
          />
        );
      } else {
        stars.push(
          <Star
            key={i}
            size={14}
            className="listing-card__star listing-card__star--empty"
            stroke="#d1d5db"
          />
        );
      }
    }
    return stars;
  };

  // Get display products (first 3-4 items)
  const displayProducts = products.slice(0, 4);

  // Handle favorite toggle using Phase 2 hook
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFavorited) {
      removeFavorite(id);
    } else {
      addFavorite({
        id,
        name,
        type: type || 'vendor',
        image: mainImage,
        slug: slug || id,
      });
    }
  };

  // Handle compare toggle using Phase 2 hook
  const handleCompareClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleItem({
      id,
      name,
      type: type || 'vendor',
      image: mainImage,
      slug: slug || id,
      rating,
      reviewCount,
      city,
      products,
    });
  };

  // Get type label styling
  const getTypeBadgeClass = () => {
    const typeClasses = {
      'farm': 'listing-card__type-badge--farm',
      'market': 'listing-card__type-badge--market',
      'csa': 'listing-card__type-badge--csa',
      'upick': 'listing-card__type-badge--upick',
      'farmstand': 'listing-card__type-badge--farmstand',
      'organic': 'listing-card__type-badge--organic'
    };
    return typeClasses[type] || 'listing-card__type-badge--default';
  };

  // Format type for display
  const formatType = (type) => {
    const typeLabels = {
      'farm': 'Farm',
      'market': 'Farmers Market',
      'csa': 'CSA Program',
      'upick': 'U-Pick',
      'farmstand': 'Farm Stand',
      'organic': 'Organic Producer'
    };
    return typeLabels[type] || type;
  };

  // Base card class with variant modifier
  const cardClass = `listing-card listing-card--${variant}`;

  return (
    <div className={cardClass}>
      {/* Image Section */}
      <div className="listing-card__image-container">
        <Link to={`/listing/${slug || id}`} className="listing-card__image-link">
          <img
            src={mainImage}
            alt={name}
            className="listing-card__image"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = placeholderImage;
            }}
          />
        </Link>

        {/* Category Badge (top-left) */}
        <div className={`listing-card__type-badge ${getTypeBadgeClass()}`}>
          <span>{formatType(type)}</span>
        </div>

        {/* Featured Badge */}
        {featured && (
          <div className="listing-card__featured-badge">
            <span>Featured</span>
          </div>
        )}

        {/* Action Buttons (top-right) */}
        <div className="listing-card__actions">
          {/* Compare Button */}
          <button
            className={`listing-card__action-btn ${isComparing ? 'listing-card__action-btn--active' : ''}`}
            onClick={handleCompareClick}
            disabled={!canAdd && !isComparing}
            aria-label={isComparing ? 'Remove from comparison' : 'Add to comparison'}
            title={isComparing ? 'Remove from comparison' : (canAdd ? 'Add to comparison' : 'Comparison full (max 4)')}
          >
            <GitCompare
              size={18}
              stroke={isComparing ? '#2D5A27' : '#ffffff'}
            />
          </button>

          {/* Favorite Heart */}
          <button
            className={`listing-card__favorite ${isFavorited ? 'listing-card__favorite--active' : ''}`}
            onClick={handleFavoriteClick}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart
              size={20}
              fill={isFavorited ? '#ef4444' : 'none'}
              stroke={isFavorited ? '#ef4444' : '#ffffff'}
            />
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="listing-card__content">
        {/* Business Name */}
        <h3 className="listing-card__name">
          <Link to={`/listing/${slug || id}`}>{name}</Link>
        </h3>

        {/* Type Label */}
        <span className="listing-card__type-label">{formatType(type)}</span>

        {/* Description */}
        {description && (
          <p className="listing-card__description">
            {truncateDescription(description)}
          </p>
        )}

        {/* Location */}
        <div className="listing-card__location">
          <MapPin size={14} className="listing-card__location-icon" />
          <span>{city || address}</span>
        </div>

        {/* Product Tags */}
        {displayProducts.length > 0 && (
          <div className="listing-card__products">
            {displayProducts.map((product, index) => (
              <span key={index} className="listing-card__product-tag">
                {product}
              </span>
            ))}
            {products.length > 4 && (
              <span className="listing-card__product-more">
                +{products.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Footer with Rating and Distance */}
        <div className="listing-card__footer">
          {/* Rating */}
          <div className="listing-card__rating">
            <div className="listing-card__stars">
              {renderStars(rating)}
            </div>
            {reviewCount > 0 && (
              <span className="listing-card__review-count">
                ({reviewCount})
              </span>
            )}
          </div>

          {/* Distance */}
          {distance && (
            <div className="listing-card__distance">
              <Navigation size={12} className="listing-card__distance-icon" />
              <span>{distance}</span>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <Link to={`/listing/${slug || id}`} className="listing-card__cta-btn">
          View Details
        </Link>
      </div>
    </div>
  );
};

export default ListingCard;
